import { useState, useRef, useMemo } from "react";
import { Check, X, Lightbulb, ArrowRight } from "lucide-react";

export default function QuizPlayer({ game, onComplete }) {
  const questions = useMemo(
    () => (game.questions || []).filter((q) => q.question?.trim() && q.options.filter((o) => o.trim()).length >= 2),
    [game]
  );
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const correctRef = useRef(0);
  const start = useRef(Date.now());

  if (questions.length === 0) {
    return <p className="text-center text-xl font-patient text-gray-500 mt-10">Ce quiz n'a pas encore de questions.</p>;
  }

  const q = questions[idx];
  const total = questions.length;

  const advance = () => {
    if (idx + 1 >= total) {
      const duration = Math.round((Date.now() - start.current) / 1000);
      const score = Math.round((correctRef.current / total) * 100);
      onComplete({ score, accuracy: score, duration });
    } else {
      setIdx((i) => i + 1);
      setPicked(null);
      setShowTip(false);
    }
  };

  const pick = (oi) => {
    if (picked !== null) return;
    const good = oi === q.answer;
    if (good) correctRef.current += 1;
    setPicked({ oi, good });
    if (q.tip?.trim()) {
      setTimeout(() => setShowTip(true), 500);
    } else {
      setTimeout(advance, 1100);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto text-center" data-testid="quiz-player">
      <p className="text-lg font-care text-emerald-700 font-bold mb-3">Question {idx + 1} / {total}</p>
      <p className="text-3xl font-patient font-black text-gray-800 mb-8">{q.question}</p>
      <div className="grid grid-cols-1 gap-4">
        {q.options.map((opt, oi) => {
          if (!opt.trim()) return null;
          const state = picked
            ? oi === q.answer ? "good" : picked.oi === oi ? "bad" : "idle"
            : "idle";
          return (
            <button
              key={oi}
              onClick={() => pick(oi)}
              data-testid={`quiz-option-${oi}`}
              className={`min-h-[4.5rem] rounded-2xl border-4 px-6 font-patient font-bold text-2xl flex items-center justify-between gap-3 active:scale-[0.98] transition-transform duration-150 ${
                state === "good" ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                : state === "bad" ? "border-red-300 bg-red-50 text-red-600"
                : "border-emerald-700/20 bg-white text-gray-800"}`}
            >
              <span>{opt}</span>
              {state === "good" && <Check className="w-7 h-7" />}
              {state === "bad" && <X className="w-7 h-7" />}
            </button>
          );
        })}
      </div>

      {showTip && q.tip?.trim() && (
        <div className="mt-8 soft-in" data-testid="quiz-tip-bubble">
          <div className="relative rounded-3xl bg-teal-50 border-4 border-teal-200 p-6 text-left">
            <div className="flex items-center gap-2 mb-2 text-teal-700 font-patient font-bold text-lg">
              <Lightbulb className="w-6 h-6" /> Le saviez-vous ?
            </div>
            <p className="font-patient text-lg text-gray-700 whitespace-pre-wrap">{q.tip}</p>
          </div>
          <button
            onClick={advance}
            data-testid="quiz-continue-button"
            className="mt-5 h-16 px-10 rounded-2xl bg-emerald-700 text-white font-patient font-bold text-xl inline-flex items-center gap-2 active:scale-95 transition-transform duration-150"
          >
            Continuer <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
